import {
  Avatar,
  Button,
  Card,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { toast } from 'react-toastify'
import { profileApi } from '../../../../api/profile'
import { teamsApi } from '../../../../api/teams'
import { usersApi } from '../../../../api/users'
import { extractApiErrorMessage } from '../../../../utils/api'
import { SettingsSectionShell } from './SettingsSectionShell'

export const BlockingAndCommunicationSection = () => {
  const { data: blockedUsers = [], isLoading: blockedUsersLoading } = profileApi.useGetBlockedUsersQuery()
  const { data: blockedTeams = [], isLoading: blockedTeamsLoading } = profileApi.useGetBlockedTeamsQuery()
  const [blockUser, blockUserState] = profileApi.useBlockUserMutation()
  const [unblockUser, unblockUserState] = profileApi.useUnblockUserMutation()
  const [blockTeam, blockTeamState] = profileApi.useBlockTeamMutation()
  const [unblockTeam, unblockTeamState] = profileApi.useUnblockTeamMutation()
  const [searchUsers, usersSearchState] = usersApi.useLazyGetUsersPageQuery()
  const [searchTeams, teamsSearchState] = teamsApi.useLazySearchTeamsQuery()

  const [userQuery, setUserQuery] = useState('')
  const [teamQuery, setTeamQuery] = useState('')

  const handleUserSearch = async () => {
    const query = userQuery.trim()
    if (!query) return

    try {
      await searchUsers({ query, limit: 10 }).unwrap()
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось найти пользователей')
    }
  }

  const handleTeamSearch = async () => {
    const query = teamQuery.trim()
    if (!query) return

    try {
      await searchTeams({ query }).unwrap()
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось найти команды')
    }
  }

  const handleBlockUser = async (userId: number) => {
    try {
      await blockUser({ userId }).unwrap()
      toast.success('Пользователь заблокирован')
      setUserQuery('')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось заблокировать пользователя')
    }
  }

  const handleBlockTeam = async (teamId: number) => {
    try {
      await blockTeam({ teamId }).unwrap()
      toast.success('Команда заблокирована')
      setTeamQuery('')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось заблокировать команду')
    }
  }

  const handleUnblockUser = async (userId: number) => {
    try {
      await unblockUser({ userId }).unwrap()
      toast.success('Блокировка пользователя снята')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось снять блокировку')
    }
  }

  const handleUnblockTeam = async (teamId: number) => {
    try {
      await unblockTeam({ teamId }).unwrap()
      toast.success('Блокировка команды снята')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось снять блокировку')
    }
  }

  const foundUsers = usersSearchState.data?.items ?? []
  const foundTeams = teamsSearchState.data ?? []
  const blockedUserIds = new Set(blockedUsers.map((item) => item.id))
  const blockedTeamIds = new Set(blockedTeams.map((item) => item.id))
  const isBusy =
    blockUserState.isLoading ||
    unblockUserState.isLoading ||
    blockTeamState.isLoading ||
    unblockTeamState.isLoading

  return (
    <SettingsSectionShell
      title="Блокировки"
      description="После блокировки мы перестанем показывать вам этого пользователя или эту команду в уведомлениях и в открытых каталогах, где блокировка уже поддерживается."
    >
      <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="black-14-medium">Найти пользователя для блокировки</Typography>
          <Typography variant="black-14-normal" color="text.secondary">
            Сейчас это работает для уведомлений, счётчика непрочитанного, списка пользователей и списка команд.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: '100%', maxWidth: 680 }}>
            <TextField
              fullWidth
              size="small"
              value={userQuery}
              onChange={(e) => setUserQuery(e.target.value)}
              placeholder="Введите @slug или имя"
            />
            <Button
              variant="follow"
              size="small"
              onClick={() => void handleUserSearch()}
              disabled={!userQuery.trim() || isBusy}
              sx={{ minWidth: 72, height: 40, flexShrink: 0 }}
            >
              Найти
            </Button>
          </Stack>
          {usersSearchState.isFetching && <CircularProgress size={18} />}
          {!usersSearchState.isUninitialized && !usersSearchState.isFetching && foundUsers.length === 0 && (
            <Typography variant="black-14-normal" color="text.secondary">
              По этому запросу пользователи не найдены.
            </Typography>
          )}
          {foundUsers.length > 0 && (
            <List disablePadding>
              {foundUsers.map((user) => (
                <ListItem
                  key={user.id}
                  secondaryAction={
                    <Button
                      variant="follow"
                      disabled={blockedUserIds.has(user.id) || isBusy}
                      onClick={() => void handleBlockUser(user.id)}
                    >
                      {blockedUserIds.has(user.id) ? 'Уже заблокирован' : 'Заблокировать'}
                    </Button>
                  }
                  sx={{ px: 0 }}
                >
                  <ListItemAvatar>
                    <Avatar src={user.avatar ?? undefined} />
                  </ListItemAvatar>
                  <ListItemText primary={user.shortname || user.fullname} secondary={user.slug ? `@${user.slug}` : ''} />
                </ListItem>
              ))}
            </List>
          )}
        </Stack>
      </Card>

      <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="black-14-medium">Найти команду для блокировки</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ width: '100%', maxWidth: 680 }}>
            <TextField
              fullWidth
              size="small"
              value={teamQuery}
              onChange={(e) => setTeamQuery(e.target.value)}
              placeholder="Введите slug или название команды"
            />
            <Button
              variant="follow"
              size="small"
              onClick={() => void handleTeamSearch()}
              disabled={!teamQuery.trim() || isBusy}
              sx={{ minWidth: 72, height: 40, flexShrink: 0 }}
            >
              Найти
            </Button>
          </Stack>
          {teamsSearchState.isFetching && <CircularProgress size={18} />}
          {!teamsSearchState.isUninitialized && !teamsSearchState.isFetching && foundTeams.length === 0 && (
            <Typography variant="black-14-normal" color="text.secondary">
              По этому запросу команды не найдены.
            </Typography>
          )}
          {foundTeams.length > 0 && (
            <List disablePadding>
              {foundTeams.map((team) => (
                <ListItem
                  key={team.id}
                  secondaryAction={
                    <Button
                      variant="follow"
                      disabled={blockedTeamIds.has(team.id) || isBusy}
                      onClick={() => void handleBlockTeam(team.id)}
                    >
                      {blockedTeamIds.has(team.id) ? 'Уже заблокирована' : 'Заблокировать'}
                    </Button>
                  }
                  sx={{ px: 0 }}
                >
                  <ListItemAvatar>
                    <Avatar src={team.image || undefined} variant="rounded" />
                  </ListItemAvatar>
                  <ListItemText primary={team.title} secondary={team.slug ? `${team.slug}${team.city ? `, ${team.city}` : ''}` : team.city} />
                </ListItem>
              ))}
            </List>
          )}
        </Stack>
      </Card>

      <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="black-14-medium">Заблокированные пользователи</Typography>
          {blockedUsersLoading ? (
            <CircularProgress size={18} />
          ) : blockedUsers.length === 0 ? (
            <Typography variant="black-14-normal" color="text.secondary">
              Пока никого нет в списке блокировок.
            </Typography>
          ) : (
            <List disablePadding>
              {blockedUsers.map((user, index) => (
                <Stack key={user.id}>
                  <ListItem
                    secondaryAction={
                      <Button variant="follow" disabled={isBusy} onClick={() => void handleUnblockUser(user.id)}>
                        Снять
                      </Button>
                    }
                    sx={{ px: 0 }}
                  >
                    <ListItemAvatar>
                      <Avatar src={user.avatar ?? undefined} />
                    </ListItemAvatar>
                    <ListItemText primary={user.shortname || user.fullname} secondary={user.slug ? `@${user.slug}` : ''} />
                  </ListItem>
                  {index < blockedUsers.length - 1 && <Divider />}
                </Stack>
              ))}
            </List>
          )}
        </Stack>
      </Card>

      <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Typography variant="black-14-medium">Заблокированные команды</Typography>
          {blockedTeamsLoading ? (
            <CircularProgress size={18} />
          ) : blockedTeams.length === 0 ? (
            <Typography variant="black-14-normal" color="text.secondary">
              Пока нет заблокированных команд.
            </Typography>
          ) : (
            <List disablePadding>
              {blockedTeams.map((team, index) => (
                <Stack key={team.id}>
                  <ListItem
                    secondaryAction={
                      <Button variant="follow" disabled={isBusy} onClick={() => void handleUnblockTeam(team.id)}>
                        Снять
                      </Button>
                    }
                    sx={{ px: 0 }}
                  >
                    <ListItemAvatar>
                      <Avatar src={team.image ?? undefined} variant="rounded" />
                    </ListItemAvatar>
                    <ListItemText primary={team.name} secondary={team.slug ? `${team.slug}${team.location ? `, ${team.location}` : ''}` : team.location} />
                  </ListItem>
                  {index < blockedTeams.length - 1 && <Divider />}
                </Stack>
              ))}
            </List>
          )}
        </Stack>
      </Card>
    </SettingsSectionShell>
  )
}
